import { Controller } from 'stimulus';

// The maintenance form (maintenances/_form): Repeats and Status as toggles
// backed by hidden fields, day buttons backed by checkboxes, the rows that
// apply to the period, and the next five due dates from
// MaintenancesController#schedule_preview as the fields change. Inputs that
// don't apply are disabled, so they neither submit nor block it. Set days or
// a monthly weekday follow the calendar, so the late-work choice gives way to
// a note.
export default class extends Controller {
  static targets = ['frequency', 'repeat', 'scheduled', 'number', 'period', 'words', 'frequentNote',
    'daysRow', 'dayButton', 'day', 'monthRow', 'monthOptions', 'due', 'preview', 'dates',
    'lateRow', 'lateChoices', 'calendarNote', 'active', 'status', 'activeHint', 'pausedHint'];

  connect() {
    this.apply();
    if (this.scheduled() && this.dueTarget.value) this.preview();
  }

  setFrequency(event) {
    this.frequencyTarget.value = event.currentTarget.dataset.value;
    this.repeatTargets.forEach((button) => button.setAttribute('aria-pressed', button === event.currentTarget));
    this.changed();
  }

  setStatus(event) {
    const active = event.currentTarget.dataset.value === 'true';
    this.activeTarget.value = active;
    this.statusTargets.forEach((button) => button.setAttribute('aria-pressed', button === event.currentTarget));
    this.activeHintTarget.classList.toggle('hidden', !active);
    this.pausedHintTarget.classList.toggle('hidden', active);
  }

  toggleDay(event) {
    const button = event.currentTarget;
    const on = button.getAttribute('aria-pressed') !== 'true';
    button.setAttribute('aria-pressed', on);
    const box = this.dayTargets.find((day) => day.value === button.dataset.day);
    if (box) box.checked = on;
    this.changed();
  }

  changed() {
    this.apply();
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.preview(), 250);
  }

  scheduled() {
    return this.frequencyTarget.value === 'custom';
  }

  apply() {
    const scheduled = this.scheduled();
    const period = this.periodTarget.value;
    const weekly = scheduled && period === 'weeks';
    const monthly = scheduled && period === 'months';

    [this.scheduledTarget, this.lateRowTarget, this.previewTarget].forEach((el) => el.classList.toggle('hidden', !scheduled));
    this.daysRowTarget.classList.toggle('hidden', !weekly);
    this.monthRowTarget.classList.toggle('hidden', !monthly);

    this.setDisabled([this.numberTarget, this.periodTarget], !scheduled);
    this.setDisabled(this.dayTargets, !weekly);
    this.setDisabled(this.monthRadios(), !monthly);
    this.setDisabled(this.lateChoicesTarget.querySelectorAll('input'), !scheduled);

    const onDays = weekly && this.dayTargets.some((day) => day.checked);
    const pattern = (this.monthRadios().find((radio) => radio.checked) || {}).value;
    const calendar = onDays || (monthly && (pattern === 'weekday' || pattern === 'last_weekday'));
    this.lateChoicesTarget.classList.toggle('hidden', calendar);
    this.calendarNoteTarget.classList.toggle('hidden', !calendar);
  }

  monthRadios() {
    return Array.from(this.monthOptionsTarget.querySelectorAll('input[type=radio]'));
  }

  setDisabled(inputs, disabled) {
    Array.from(inputs).forEach((input) => { input.disabled = disabled; });
  }

  preview() {
    if (!this.scheduled() || !this.dueTarget.value) {
      this.datesTarget.innerHTML = '';
      return;
    }

    const params = new URLSearchParams();
    new FormData(this.element).forEach((value, name) => {
      if (name.startsWith('maintenance[') && name !== 'maintenance[additional_details]') params.append(name, value);
    });
    fetch(`${this.data.get('previewUrl')}?${params}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => { if (data) this.show(data); })
      .catch(() => {});
  }

  show(data) {
    this.wordsTarget.textContent = data.words || '';
    this.frequentNoteTarget.classList.toggle('hidden', !data.frequent);
    this.datesTarget.innerHTML = '';
    (data.dates || []).forEach((date) => {
      const item = document.createElement('li');
      item.textContent = date;
      this.datesTarget.appendChild(item);
    });
    this.showMonthly(data.monthly || []);
  }

  // Rebuilds the monthly choices for the due date, keeping the one picked
  // when it still applies.
  showMonthly(options) {
    const picked = (this.monthRadios().find((radio) => radio.checked) || {}).value || this.monthOptionsTarget.dataset.selected;
    const keep = options.some((option) => option.value === picked) ? picked : 'date';
    this.monthOptionsTarget.innerHTML = '';
    options.forEach((option) => {
      const label = document.createElement('label');
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'maintenance[monthly_on]';
      radio.value = option.value;
      radio.checked = option.value === keep;
      radio.dataset.action = 'change->schedule-form#changed';
      label.appendChild(radio);
      label.appendChild(document.createTextNode(` ${option.label}`));
      this.monthOptionsTarget.appendChild(label);
    });
    this.apply();
  }
}
